import { escape, isEmpty } from "lodash-es";
import type { Instance, Placement, Props } from "tippy.js";
import { computed, Ref, ref, watch } from "vue";
import { useTippy } from "vue-tippy";

import * as inject from "@/components/inject";
import { in_mac } from "@/utils/in";

export type Tooltip = Instance<Props>;
export type TooltipItem = {
  text?: string;
  shortcut?: string;
  detail?: string;
};
export type TooltipContent = string | TooltipItem | TooltipItem[] | undefined;
/**
 * Tooltip bound to `el`, shown/hidden manually via the returned functions.
 *
 * Owns a tippy instance and a theme watcher, freed by the returned `release`.
 *
 * Note: should the app ever use `<KeepAlive>`, deactivated components would
 * additionally need `onDeactivated(hideTooltip)` — tippy renders outside the
 * component's DOM subtree, so a cached-away component would leave its open
 * tooltip floating. No component does this today: the app has no keep-alive,
 * so the hook would be dead code.
 */
export const useTooltip = (
  // HTML or SVG hosts (e.g. chart segments) — tippy anchors to any Element.
  el: Ref<HTMLElement | SVGElement | undefined>,
  data: Ref<TooltipContent>,
  placement: Placement,
  isSetupAllowed = ref(true),
  showArrow = false
) => {
  const theme = inject.theme();
  const itemContent = (item: TooltipItem): string => {
    const hasText = item.text && !isEmpty(item.text);
    const hasShortcut = item.shortcut && !isEmpty(item.shortcut);
    const hasDetail = item.detail && !isEmpty(item.detail);
    const shortcutDisplay = hasShortcut
      ? `<span class="shortcut${hasText ? " hasText" : ""}${
          in_mac() ? " mac" : ""
        }">${item.shortcut}</span>`
      : "";
    const primary =
      hasText && hasShortcut
        ? item.text + shortcutDisplay
        : hasText
          ? item.text
          : shortcutDisplay;
    const detailDisplay = hasDetail
      ? `<span class="detail">${escape(item.detail)}</span>`
      : "";
    return [primary, detailDisplay]
      .filter((value) => !isEmpty(value))
      .join("<br />");
  };
  const content = computed<string | undefined>(() => {
    if (!data.value) return;
    // Handle array of records: [{text, shortcut}, {...}]
    else if (Array.isArray(data.value)) {
      return data.value
        .map(itemContent)
        .filter((item) => !isEmpty(item))
        .join("<br />");
    } else if (data.value instanceof Object) {
      return itemContent(data.value) || undefined;
    } else return data.value;
  });
  const hasContent = computed(() => !isEmpty(content.value));
  const tooltip = ref<Tooltip | undefined>();
  let showTimeout: number | undefined;
  const showTooltip = () => {
    if (!isSetupAllowed.value || !hasContent.value) return;
    // The tippy instance stays bound to the element it was created on. When the
    // target ref points to a different element by now — a shared tooltip moved
    // across `v-for` items, or the original node was replaced by a re-render —
    // drop the stale instance so it is recreated on the current element.
    if (tooltip.value && el.value && tooltip.value.reference !== el.value) {
      tooltip.value.popperInstance?.destroy();
      tooltip.value.destroy();
      tooltip.value = undefined;
    }
    if (!tooltip.value) setTooltip();
    if (!tooltip.value) return;

    if (showTimeout) {
      window.clearTimeout(showTimeout);
    }
    showTimeout = window.setTimeout(() => {
      if (!isSetupAllowed.value || !hasContent.value) return;
      tooltip.value?.show();
    }, 700);
  };
  const hideTooltip = () => {
    if (showTimeout) {
      window.clearTimeout(showTimeout);
    }
    showTimeout = undefined;
    tooltip.value?.hide();
  };
  // Stop handles of the theme watchers created by setTooltip() (one per
  // created tippy instance), stopped on release().
  const stopThemeWatchers: Array<() => void> = [];
  const setTooltip = () => {
    if (!isSetupAllowed.value || !hasContent.value) return;
    const { tippy } = useTippy(el, {
      content,
      placement,
      trigger: "manual",
      hideOnClick: false,
      showOnCreate: false,
      arrow: showArrow,
      animation: "fade",
      duration: 200,
      allowHTML:
        Array.isArray(data.value) ||
        (data.value instanceof Object &&
          ("shortcut" in data.value || "detail" in data.value)),
      theme: theme.value,
    });
    stopThemeWatchers.push(
      watch(theme, (theme) => {
        tippy.value?.setProps({ theme });
      })
    );
    tooltip.value = tippy.value;
  };
  const release = () => {
    if (showTimeout) {
      window.clearTimeout(showTimeout);
    }
    showTimeout = undefined;
    stopThemeWatchers.forEach((stop) => stop());
    stopThemeWatchers.length = 0;
    if (!tooltip.value) return;
    tooltip.value.popperInstance?.destroy();
    tooltip.value.destroy();
    tooltip.value = undefined;
  };
  return {
    showTooltip,
    hideTooltip,
    release,
  };
};
